import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, ArrowLeft, Home, Building2, Landmark } from 'lucide-react';
import { useCustomPageTitle } from '../../hooks/usePageTitle';
import { getProjectQuestions, getProjectMapping, AIQuestion, AISelectedItem } from '../../services/aiAssistantService';
import { AI_CATALOG } from '../../data/ai-catalog';
import { useAuth } from '../../contexts/AuthContext';
import { getProperties, createProperty } from '../../services/propertyService';
import { getEstimates } from '../../services/estimateService';
import { Property } from '../../types/property';

// ── Category labels ───────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  preparation:  'Préparation & Études',
  demolition:   'Démolition',
  grosoeuvre:   'Gros œuvre',
  vrd:          'VRD (Voiries et Réseaux Divers)',
  isolation:    'Isolation et Étanchéité',
  cloisons:     'Cloisons & Aménagement',
  technique:    'Électricité, Chauffage, Ventilation',
  sanitaire:    'Sanitaire et Hygiène',
  revetements:  'Revêtements',
  menuiserie:   'Menuiseries',
  finitions:    'Finitions',
  exterieurs:   'Extérieurs',
};

// ── Suggestion chips ──────────────────────────────────────────────────────────

const SUGGESTION_CHIPS = [
  'Combles en chambre',
  'Rénover ma salle de bain',
  'Améliorer mon DPE',
  'Rafraîchir la décoration',
  'Changer mes sols',
  'Créer une pièce supplémentaire',
];

// ── Shared styles ─────────────────────────────────────────────────────────────

const primaryBtn: React.CSSProperties = {
  background: 'var(--gradient-primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const primaryBtnDisabled: React.CSSProperties = {
  ...primaryBtn,
  opacity: 0.45,
  cursor: 'not-allowed',
};

const toggleBase: React.CSSProperties = {
  padding: '7px 16px',
  fontSize: 13,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  border: '0.5px solid var(--color-border)',
  background: 'white',
  color: '#1E1148',
  fontWeight: 400,
  transition: 'all 0.15s',
};

const toggleActive: React.CSSProperties = {
  ...toggleBase,
  border: '0.5px solid rgba(109,40,217,0.35)',
  background: '#F5F3FF',
  color: '#4C1D95',
  fontWeight: 500,
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface QuestionBlockProps {
  question: AIQuestion;
  index: number;
  answer: string;
  onAnswer: (value: string) => void;
}

const QuestionBlock: React.FC<QuestionBlockProps> = ({ question, index, answer, onAnswer }) => (
  <div style={{ borderBottom: '0.5px solid var(--color-border)', paddingBottom: 18 }}>
    <p style={{ fontSize: 14, fontWeight: 500, color: '#1E1148', marginBottom: 10 }}>
      {index + 1}. {question.text}
    </p>

    {question.type === 'oui_non' && (
      <div style={{ display: 'flex', gap: 8 }}>
        {['Oui', 'Non'].map((opt) => (
          <button
            key={opt}
            style={answer === opt ? toggleActive : toggleBase}
            onClick={() => onAnswer(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    )}

    {question.type === 'choix' && question.options && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {question.options.map((opt) => (
          <button
            key={opt}
            style={answer === opt ? toggleActive : toggleBase}
            onClick={() => onAnswer(opt)}
          >
            {opt}
          </button>
        ))}
      </div>
    )}

    {question.type === 'numerique' && (
      <input
        type="number"
        value={answer}
        onChange={(e) => onAnswer(e.target.value)}
        placeholder="Valeur..."
        min={0}
        style={{
          border: '0.5px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 12px',
          fontSize: 14,
          color: '#1E1148',
          width: 160,
          outline: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-primary)';
          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(61,26,120,0.08)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
    )}

    <p style={{ fontSize: 11, color: '#7C6FA0', fontStyle: 'italic', marginTop: 8 }}>
      {question.why}
    </p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

// ── Property item ─────────────────────────────────────────────────────────────

interface UnifiedProperty {
  propertyId: string | null;
  address: string;
  postcode: string;
  city: string;
  type: Property['type'] | null;
  area: number | null;
  isOrphan: boolean;
}

const PROPERTY_TYPE_LABELS: Record<NonNullable<Property['type']>, string> = {
  apartment: 'Appartement',
  house: 'Maison',
  building: 'Immeuble',
};
const PROPERTY_TYPE_ICONS: Record<NonNullable<Property['type']>, React.FC<{ size: number }>> = {
  apartment: ({ size }) => <Home size={size} strokeWidth={1.5} />,
  house: ({ size }) => <Landmark size={size} strokeWidth={1.5} />,
  building: ({ size }) => <Building2 size={size} strokeWidth={1.5} />,
};

const AIAssistant: React.FC = () => {
  useCustomPageTitle('SimulReno AI');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<'input' | 'questions' | 'review'>('input');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<AIQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [suggestedItems, setSuggestedItems] = useState<AISelectedItem[]>([]);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOverloaded, setIsOverloaded] = useState(false);
  const [retryFn, setRetryFn] = useState<(() => Promise<void>) | null>(null);

  // ── Unified property list (mirrors EstimatesList "Biens à estimer" logic) ──
  const [unifiedProperties, setUnifiedProperties] = useState<UnifiedProperty[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<UnifiedProperty | null>(null); // null = nouveau bien

  useEffect(() => {
    if (!user) { setPropertiesLoading(false); return; }

    Promise.all([getProperties(user.uid), getEstimates(user.uid)])
      .then(([props, estimates]) => {
        // All registered properties — including those already estimated (estimateId set).
        // The dashboard now shows all properties regardless of estimateId,
        // so the AI assistant must mirror this: a property never disappears.
        const fromProps: UnifiedProperty[] = props
          .map(p => ({
            propertyId: p.id,
            address: p.address,
            postcode: p.postcode,
            city: p.city,
            type: p.type,
            area: p.area,
            isOrphan: false,
          }));

        // Orphan estimates: detailed estimates with location but no matching Property record
        // (mirrors EstimatesList "Biens orphelins" logic — these are NOT deleted properties
        // because handleConfirmDeleteProperty cascades deletion to linked estimates)
        const fromOrphans: UnifiedProperty[] = estimates
          .filter((e) => {
            if ((e as any).type !== 'detailed') return false;
            const loc = (e as any).description?.location;
            if (!loc?.address || !loc?.postcode) return false;
            return !props.some(
              p => p.address === loc.address && p.postcode === loc.postcode
            );
          })
          .map((e) => {
            const loc = (e as any).description?.location;
            return {
              propertyId: null,
              address: loc.address,
              postcode: loc.postcode,
              city: loc.city ?? '',
              type: (e as any).description?.propertyType ?? null,
              area: (e as any).description?.area ?? null,
              isOrphan: true,
            };
          });

        setUnifiedProperties([...fromProps, ...fromOrphans]);
      })
      .catch(() => setUnifiedProperties([]))
      .finally(() => setPropertiesLoading(false));
  }, [user?.uid]); // eslint-disable-line

  // ── Input step handlers ────────────────────────────────────────────────────

  const handleAnalyze = async () => {
    if (!description.trim() || loading) return;
    setError(null);
    setIsOverloaded(false);
    setLoading(true);
    try {
      const qs = await getProjectQuestions(description.trim());
      setQuestions(qs);
      setAnswers({});
      setStep('questions');
    } catch (err: any) {
      if ((err as any).code === 'service_unavailable') {
        setIsOverloaded(true);
        setRetryFn(() => handleAnalyze);
      } else {
        setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Questions step handlers ────────────────────────────────────────────────

  const handleGetMapping = async () => {
    if (loading) return;
    setError(null);
    setIsOverloaded(false);
    setLoading(true);
    try {
      const answersForServer: Record<string, string> = {};
      questions.forEach((q, i) => {
        if (answers[i] !== undefined && answers[i] !== '') {
          answersForServer[q.text] = answers[i];
        }
      });
      const items = await getProjectMapping(description.trim(), answersForServer);
      setSuggestedItems(items);
      setCheckedItems(new Set(items.map((i) => i.key)));
      setStep('review');
    } catch (err: any) {
      if ((err as any).code === 'service_unavailable') {
        setIsOverloaded(true);
        setRetryFn(() => handleGetMapping);
      } else {
        setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Review step handlers ───────────────────────────────────────────────────

  const toggleItem = (key: string) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleLaunchEstimate = async () => {
    const selectedItems = suggestedItems.filter((i) => checkedItems.has(i.key));

    if (selectedItem?.propertyId) {
      // Registered property → skip step 1, no credit consumed
      navigate('/estimation/nouvelle', {
        state: { aiPreselectedItems: selectedItems, fromAI: true,
                 existingPropertyId: selectedItem.propertyId },
      });
    } else if (selectedItem?.isOrphan && user) {
      // Orphan estimate = bien déjà payé historiquement.
      // On crée le Property record manquant (sans crédit) puis on traite
      // ce bien exactement comme un bien enregistré.
      setLoading(true);
      try {
        const propertyId = await createProperty(user.uid, {
          userId: user.uid,
          address: selectedItem.address,
          postcode: selectedItem.postcode,
          city: selectedItem.city,
          type: selectedItem.type ?? 'apartment',
          area: selectedItem.area ?? 0,
          createdAt: new Date().toISOString(),
          lastEstimateAt: null,
          estimateId: null,
          creditConsumed: true,
        });
        navigate('/estimation/nouvelle', {
          state: { aiPreselectedItems: selectedItems, fromAI: true, existingPropertyId: propertyId },
        });
      } catch {
        // Fallback : pré-remplissage step 1 si la création échoue
        navigate('/estimation/nouvelle', {
          state: { aiPreselectedItems: selectedItems, fromAI: true,
                   prefillAddress: { address: selectedItem.address, postcode: selectedItem.postcode, city: selectedItem.city } },
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Nouveau bien
      navigate('/estimation/nouvelle', {
        state: { aiPreselectedItems: selectedItems, fromAI: true },
      });
    }
  };

  // ── Group suggested items by category ─────────────────────────────────────

  const groupedSuggested = React.useMemo(() => {
    const catalogMap = new Map(AI_CATALOG.map((c) => [c.key, c]));
    const groups: Record<string, AISelectedItem[]> = {};
    suggestedItems.forEach((item) => {
      const cat = catalogMap.get(item.key)?.category ?? 'autres';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  }, [suggestedItems]);

  // ── Card wrapper ───────────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    maxWidth: 680,
    margin: '0 auto',
    padding: '24px 16px',
  };

  const innerCard: React.CSSProperties = {
    background: 'white',
    border: '0.5px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    padding: 28,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={cardStyle}>
      {/* Overloaded banner */}
      {isOverloaded && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: '#FFFBEB',
            border: '0.5px solid rgba(217,119,6,0.3)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <p style={{ fontSize: 13, fontWeight: 500, color: '#92400E', marginBottom: 10 }}>
            Le service IA est momentanément surchargé. Votre projet n'a pas été perdu.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              style={{ ...primaryBtn, padding: '8px 16px', fontSize: 13 }}
              disabled={loading}
              onClick={() => retryFn && retryFn()}
            >
              {loading ? 'Nouvelle tentative…' : 'Réessayer'}
            </button>
            <button
              style={{
                background: 'none',
                border: 'none',
                color: '#7C6FA0',
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
              onClick={() => navigate('/estimation/nouvelle')}
            >
              Continuer sans l'assistant →
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: '8px 14px',
            background: '#FEF2F2',
            border: '0.5px solid rgba(220,38,38,0.3)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 12,
            color: '#DC2626',
          }}
        >
          {error}
        </div>
      )}

      {/* ── INPUT ──────────────────────────────────────────────────────────── */}
      {step === 'input' && (
        <div style={innerCard}>
          {/* Badge */}
          <div style={{ marginBottom: 16 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#EEEDFE',
                color: '#3C3489',
                fontSize: 12,
                fontWeight: 500,
                padding: '4px 10px',
                borderRadius: 99,
              }}
            >
              <Sparkles size={13} strokeWidth={1.75} />
              SimulReno AI
            </span>
          </div>

          <h1 style={{ fontSize: 20, fontWeight: 500, color: '#1E1148', marginBottom: 6 }}>
            Décrivez votre projet
          </h1>
          <p style={{ fontSize: 13, color: '#7C6FA0', marginBottom: 20 }}>
            Notre assistant analyse votre description et sélectionne les travaux adaptés.
          </p>

          {/* Property selection — only shown when the user has known properties/orphans */}
          {(propertiesLoading || unifiedProperties.length > 0) && (
            <div
              style={{
                marginBottom: 22,
                padding: '14px 16px',
                background: 'var(--color-surface)',
                border: '0.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <p style={{ fontSize: 12, fontWeight: 600, color: '#1E1148', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Quel bien est concerné ?
              </p>

              {propertiesLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#7C6FA0', fontSize: 12 }}>
                  <Loader2 size={13} strokeWidth={2} className="animate-spin" />
                  Chargement de vos biens…
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {/* Unified property + orphan cards */}
                  {unifiedProperties.map((item, idx) => {
                    const active = selectedItem === item;
                    const Icon = item.type ? PROPERTY_TYPE_ICONS[item.type] : Building2;
                    const label = item.type ? PROPERTY_TYPE_LABELS[item.type] : 'Bien';
                    const shortAddr = item.address.length > 32
                      ? item.address.slice(0, 32) + '…'
                      : item.address;
                    return (
                      <button
                        key={item.propertyId ?? `orphan-${idx}`}
                        style={{
                          ...(active ? toggleActive : toggleBase),
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 7,
                          padding: '7px 12px',
                          textAlign: 'left',
                        }}
                        onClick={() => setSelectedItem(active ? null : item)}
                        title={`${item.address}, ${item.postcode} ${item.city}`}
                      >
                        <Icon size={13} />
                        <span>
                          <span style={{ display: 'block', fontSize: 12, fontWeight: 500 }}>
                            {shortAddr}
                          </span>
                          <span style={{ display: 'block', fontSize: 11, opacity: 0.7 }}>
                            {label}{item.area ? ` · ${item.area} m²` : ''}
                          </span>
                        </span>
                      </button>
                    );
                  })}

                  {/* "Nouveau bien" option */}
                  <button
                    style={{
                      ...(selectedItem === null ? toggleActive : toggleBase),
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 12px',
                    }}
                    onClick={() => setSelectedItem(null)}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1, marginBottom: 1 }}>+</span>
                    <span>
                      <span style={{ display: 'block', fontSize: 12, fontWeight: 500 }}>Nouveau bien</span>
                      <span style={{ display: 'block', fontSize: 11, opacity: 0.7 }}>Créer une nouvelle propriété</span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Textarea */}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex : J'ai un appartement de 65 m² des années 70, je veux abattre une cloison pour ouvrir le salon, refaire la salle de bain et changer les fenêtres..."
            style={{
              width: '100%',
              minHeight: 110,
              border: '0.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '12px 14px',
              fontSize: 14,
              color: '#1E1148',
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              lineHeight: 1.55,
              boxSizing: 'border-box',
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(61,26,120,0.08)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />

          {/* Suggestion chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 20 }}>
            {SUGGESTION_CHIPS.map((chip) => (
              <button
                key={chip}
                onClick={() => setDescription(chip)}
                style={{
                  padding: '5px 12px',
                  fontSize: 12,
                  borderRadius: 99,
                  border: '0.5px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  color: '#7C6FA0',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(109,40,217,0.4)';
                  e.currentTarget.style.color = '#4C1D95';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = '#7C6FA0';
                }}
              >
                {chip}
              </button>
            ))}
          </div>

          {/* CTA */}
          <button
            style={!description.trim() || loading ? primaryBtnDisabled : primaryBtn}
            disabled={!description.trim() || loading}
            onClick={handleAnalyze}
          >
            {loading ? (
              <>
                <Loader2 size={15} strokeWidth={2} className="animate-spin" />
                Analyse en cours…
              </>
            ) : (
              'Analyser mon projet →'
            )}
          </button>
        </div>
      )}

      {/* ── QUESTIONS ──────────────────────────────────────────────────────── */}
      {step === 'questions' && (
        <div style={innerCard}>
          {/* Project description recap */}
          <div
            style={{
              borderLeft: '3px solid var(--color-primary)',
              background: '#EEEDFE',
              padding: '10px 14px',
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              fontSize: 13,
              fontStyle: 'italic',
              color: '#4C1D95',
              marginBottom: 22,
            }}
          >
            {description}
          </div>

          <h2 style={{ fontSize: 15, fontWeight: 500, color: '#1E1148', marginBottom: 20 }}>
            Quelques précisions pour affiner l'estimation
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {questions.map((q, i) => (
              <QuestionBlock
                key={i}
                question={q}
                index={i}
                answer={answers[i] ?? ''}
                onAnswer={(v) => setAnswers((prev) => ({ ...prev, [i]: v }))}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button
              style={{
                ...toggleBase,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 16px',
              }}
              onClick={() => { setStep('input'); setError(null); }}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Modifier
            </button>
            <button
              style={loading ? primaryBtnDisabled : primaryBtn}
              disabled={loading}
              onClick={handleGetMapping}
            >
              {loading ? (
                <>
                  <Loader2 size={15} strokeWidth={2} className="animate-spin" />
                  Sélection en cours…
                </>
              ) : (
                'Voir les travaux suggérés →'
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── REVIEW ─────────────────────────────────────────────────────────── */}
      {step === 'review' && (
        <>
          <div style={{ ...innerCard, paddingBottom: 100 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 24 }}>
              <h2 style={{ fontSize: 17, fontWeight: 500, color: '#1E1148' }}>
                Travaux suggérés pour votre projet
              </h2>
              <span style={{ fontSize: 12, color: '#7C6FA0' }}>
                {checkedItems.size} poste{checkedItems.size > 1 ? 's' : ''} sélectionné{checkedItems.size > 1 ? 's' : ''}
              </span>
            </div>

            {Object.entries(groupedSuggested).map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: 28 }}>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#7C6FA0',
                    marginBottom: 12,
                  }}
                >
                  {CATEGORY_LABELS[cat] ?? cat}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {items.map((item) => {
                    const catalogEntry = AI_CATALOG.find((c) => c.key === item.key);
                    const checked = checkedItems.has(item.key);
                    return (
                      <label
                        key={item.key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                          cursor: 'pointer',
                          padding: '10px 14px',
                          borderRadius: 'var(--radius-sm)',
                          border: `0.5px solid ${checked ? 'rgba(109,40,217,0.2)' : 'var(--color-border)'}`,
                          background: checked ? 'rgba(109,40,217,0.03)' : 'var(--color-surface)',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleItem(item.key)}
                          style={{ marginTop: 2, flexShrink: 0, accentColor: 'var(--color-primary)' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 500, color: '#1E1148' }}>
                              {catalogEntry?.name ?? item.key}
                            </span>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 99,
                                background: item.strength === 'recommended' ? 'rgba(194,81,31,0.1)' : 'rgba(123,63,160,0.1)',
                                color: item.strength === 'recommended' ? '#C2511F' : '#7B3FA0',
                              }}
                            >
                              {item.strength === 'recommended' ? 'Recommandé' : 'Optionnel'}
                            </span>
                          </div>
                          <p style={{ fontSize: 11, color: '#7C6FA0', marginTop: 3 }}>
                            {item.reason}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Fixed footer */}
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'white',
              borderTop: '0.5px solid var(--color-border)',
              padding: '14px 24px',
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              zIndex: 50,
            }}
          >
            <button
              style={{
                ...toggleBase,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 16px',
              }}
              onClick={() => { setStep('questions'); setError(null); }}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Modifier
            </button>
            <button
              style={(checkedItems.size === 0 || loading) ? primaryBtnDisabled : primaryBtn}
              disabled={checkedItems.size === 0 || loading}
              onClick={handleLaunchEstimate}
            >
              {loading ? (
                <>
                  <Loader2 size={15} strokeWidth={2} className="animate-spin" />
                  Préparation…
                </>
              ) : 'Lancer l\'estimation détaillée →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AIAssistant;
